<template>
  <div class="documentation-container">
    <div class="header-section mb-4">
      <div class="header-content">
        <h1 class="text-center">Documentation</h1>
        <div class="upload-button-container d-none d-md-block">
          <b-button variant="success" @click="openFileSelector" class="me-2">
            <i class="fas fa-upload me-2"></i>Uploader un fichier
          </b-button>
        </div>
        
        <!-- Input file caché pour la sélection -->
        <input
          ref="fileInput"
          type="file"
          accept="*/*"
          style="display: none"
          @change="handleFileSelection"
        />
      </div>
      
      <!-- Filtres de recherche -->
      <div class="search-filters mt-4">
        <div class="row">
          <div class="col-md-4 col-12 mb-3">
            <b-form-group :label="$mq === 'mobile' ? 'Recherche' : 'Recherche textuelle'">
              <b-form-input
                v-model="searchQuery"
                :placeholder="$mq === 'mobile' ? 'Rechercher...' : 'Rechercher par nom ou description...'"
                @input="debouncedSearch"
                debounce="500"
              />
            </b-form-group>
          </div>
          <div class="col-md-4 col-12 mb-3">
            <b-form-group :label="$mq === 'mobile' ? 'Type' : 'Type de fichier'">
              <b-form-select
                v-model="selectedMimeType"
                :options="mimeTypeOptions"
                @change="searchDocuments"
                :placeholder="$mq === 'mobile' ? 'Tous...' : 'Tous les types...'"
              />
            </b-form-group>
          </div>
          <div class="col-md-4 col-12 mb-3">
            <b-form-group label="Tags">
              <b-form-select
                v-model="selectedTag"
                :options="tagOptions"
                @change="searchDocuments"
                placeholder="Sélectionner un tag..."
              />
            </b-form-group>
          </div>
        </div>
      </div>
    </div>

    <!-- Actions fixes en bas pour mobile -->
    <FooterActions
        class="d-md-none"
        :buttons="[
          { text: 'Uploader un fichier', variant: 'success', onClick: openFileSelector }
        ]"
    />

    <!-- Liste des documents -->
    <div class="documentation-list">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Chargement...</span>
        </div>
        <p class="mt-3">Chargement des documents...</p>
      </div>

      <div v-else-if="documents.length === 0" class="text-center py-5">
        <i class="fas fa-file-alt fa-3x text-muted mb-3"></i>
        <p class="text-muted">Aucun document trouvé.</p>
        <p class="text-muted">Essayez de modifier vos critères de recherche.</p>
      </div>

      <div v-else class="documents-grid">
        <div
          v-for="doc in documents"
          :key="doc.id"
          class="document-card card h-100"
        >
          <div class="card-body d-flex flex-column">
            <!-- Icône du type de fichier -->
            <div class="file-icon mb-3 text-center">
              <i :class="getFileIcon(doc.type_mime)" class="fa-3x text-primary"></i>
            </div>
            
            <!-- Informations du document -->
            <h6 class="card-title text-truncate" :title="doc.nom">
              {{ doc.nom }}
            </h6>
            
            <p v-if="doc.description" class="card-text small text-muted flex-grow-1">
              {{ truncateText(doc.description, 80) }}
            </p>
            


            <!-- Tags -->
            <div v-if="doc.tags && doc.tags.length > 0" class="tags mb-3">
              <b-badge
                v-for="tag in doc.tags.slice(0, 3)"
                :key="tag"
                variant="light"
                class="me-1 mb-1"
              >
                {{ tag }}
              </b-badge>
              <span v-if="doc.tags.length > 3" class="text-muted small">
                +{{ doc.tags.length - 3 }}
              </span>
            </div>

            <!-- Boutons d'action -->
            <div class="card-actions mt-auto">
              <div class="btn-group w-100" role="group">
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="viewDocument(doc)"
                  class="flex-fill"
                >
                  <i class="fas fa-info-circle me-1"></i>Détails
                </b-button>
                <b-button
                  variant="outline-success"
                  size="sm"
                  @click="downloadDocument(doc.id)"
                  class="flex-fill"
                >
                  <i class="fas fa-download me-1"></i>Télécharger
                </b-button>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="deleteDocument(doc.id)"
                  class="flex-fill"
                >
                  <i class="fas fa-trash me-1"></i>Supprimer
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="pagination-wrapper mt-4">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalResults"
          :per-page="perPage"
          @change="handlePageChange"
          align="center"
        />
      </div>
    </div>

    <!-- Modal d'upload -->
    <b-modal
      v-model="showUploadModal"
      title="Uploader un nouveau document"
      size="lg"
      @ok="uploadDocument"
      @cancel="resetUploadForm"
      @hidden="resetUploadForm"
      :ok-disabled="!uploadForm.file || !uploadForm.nom || !uploadForm.nom.trim() || !uploadForm.type_mime || !uploadForm.type_mime.trim()"
    >
      <b-form @submit.prevent="uploadDocument">
                 <!-- Informations du fichier sélectionné -->
         <div v-if="uploadForm.file" class="file-info mb-4 p-3 bg-light rounded">
           <div class="row">
             <div class="col-md-2 text-center">
               <i :class="getFileIcon(uploadForm.type_mime)" class="fa-3x text-primary"></i>
             </div>
             <div class="col-md-10">
               <h6 class="mb-2">{{ uploadForm.file.name }}</h6>
               <div class="row text-muted small">
                 <div class="col-md-4">
                   <strong>Taille :</strong> {{ formatFileSize(uploadForm.file.size) }}
                 </div>
                 <div class="col-md-4">
                   <strong>Type :</strong> 
                   <span :class="uploadForm.type_mime ? 'text-success' : 'text-danger'">
                     {{ uploadForm.type_mime || 'Type non détecté' }}
                   </span>
                 </div>
                 <div class="col-md-4">
                   <strong>Date :</strong> {{ formatDate(new Date(uploadForm.file.lastModified)) }}
                 </div>
               </div>
               <div v-if="!uploadForm.type_mime" class="alert alert-warning mt-2 mb-0 py-2">
                 <i class="fas fa-exclamation-triangle me-2"></i>
                 Le type MIME n'a pas pu être détecté automatiquement. Veuillez le saisir manuellement.
               </div>
             </div>
           </div>
         </div>

        <b-form-group label="Nom du document" required>
          <b-form-input
            v-model="uploadForm.nom"
            placeholder="Nom du document..."
            maxlength="255"
            required
            :state="uploadForm.nom ? true : null"
          />
          <div v-if="!uploadForm.nom" class="invalid-feedback d-block">
            Le nom du document est obligatoire
          </div>
        </b-form-group>

        <b-form-group label="Description">
          <b-form-textarea
            v-model="uploadForm.description"
            placeholder="Description du document..."
            rows="3"
            maxlength="500"
          />
        </b-form-group>

        <b-form-group label="Type MIME" required>
          <b-form-input
            v-model="uploadForm.type_mime"
            placeholder="application/pdf, image/jpeg, etc."
            maxlength="100"
            required
            :state="uploadForm.type_mime ? true : null"
          />
          <div v-if="!uploadForm.type_mime" class="invalid-feedback d-block">
            Le type MIME est obligatoire
          </div>
          <small class="form-text text-muted">
            Format : type/sous-type (ex: application/pdf, image/jpeg)
          </small>
        </b-form-group>


        <b-form-group label="Tags">
          <b-form-input
            v-model="uploadForm.tags"
            placeholder="tag1,tag2,tag3"
            maxlength="200"
          />
          <small class="form-text text-muted">
            Séparez les tags par des virgules
          </small>
        </b-form-group>
      </b-form>
    </b-modal>

    <!-- Modal de visualisation du document -->
    <b-modal
      v-model="showViewModal"
      :title="selectedDocument?.nom || 'Détails du document'"
      size="xl"
      hide-footer
      @hidden="selectedDocument = null"
    >
      <div v-if="selectedDocument" class="document-details">
        <div class="row">
          <div class="col-md-8">
            <h5>Informations générales</h5>
            <table class="table table-striped">
              <tbody>
                <tr>
                  <td><strong>Nom :</strong></td>
                  <td>{{ selectedDocument.nom }}</td>
                </tr>
                <tr>
                  <td><strong>Description :</strong></td>
                  <td>{{ selectedDocument.description || 'Aucune description' }}</td>
                </tr>
                <tr>
                  <td><strong>Type MIME :</strong></td>
                  <td>{{ selectedDocument.type_mime }}</td>
                </tr>
                <tr>
                  <td><strong>Taille :</strong></td>
                  <td>{{ formatFileSize(selectedDocument.taille) }}</td>
                </tr>
                <tr>
                  <td><strong>Date de création :</strong></td>
                  <td>{{ formatDate(selectedDocument.date_creation) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="col-md-4">
            <div v-if="selectedDocument.tags && selectedDocument.tags.length > 0" class="mb-3">
              <strong>Tags :</strong>
              <div class="mt-2">
                <b-badge
                  v-for="tag in selectedDocument.tags"
                  :key="tag"
                  variant="info"
                  class="me-1 mb-1"
                >
                  {{ tag }}
                </b-badge>
              </div>
            </div>
            
            <div v-if="selectedDocument.metadata && Object.keys(selectedDocument.metadata).length > 0" class="mb-3">
              <strong>Métadonnées personnalisées :</strong>
              <pre class="bg-light p-2 rounded mt-2 small">{{ JSON.stringify(selectedDocument.metadata, null, 2) }}</pre>
            </div>

            <div class="text-center mt-4">
              <b-button
                variant="info"
                @click="viewDocumentContent(selectedDocument.id)"
                class="me-2"
              >
                <i class="fas fa-eye me-1"></i>Voir
              </b-button>
              <b-button
                variant="primary"
                @click="downloadDocument(selectedDocument.id)"
                class="me-2"
              >
                <i class="fas fa-download me-1"></i>Télécharger
              </b-button>
              <b-button
                variant="outline-secondary"
                @click="showViewModal = false"
              >
                Fermer
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import api from '@/plugins/api.js';
import FooterActions from '@/components/FooterActions.vue';

// État réactif
const loading = ref(false);
const documents = ref([]);
const totalResults = ref(0);
const totalPages = ref(0);
const currentPage = ref(1);
const perPage = ref(20);

// Référence pour l'input file
const fileInput = ref(null);

// Filtres de recherche
const searchQuery = ref('');
const selectedMimeType = ref(null);
const selectedTag = ref('');

// Modals
const showUploadModal = ref(false);
const showViewModal = ref(false);
const selectedDocument = ref(null);

// Formulaire d'upload
const uploadForm = ref({
  file: null,
  nom: '',
  description: '',
  tags: '',
  type_mime: ''
});

// Options pour les selects
const availableTags = ref([]);

const mimeTypeOptions = computed(() => {
  const types = [...new Set(documents.value.map(d => d.type_mime).filter(t => t))];
  
  // Types MIME communs pour une meilleure UX
  const commonTypes = [
    { value: 'application/pdf', text: 'PDF' },
    { value: 'image/jpeg', text: 'JPEG' },
    { value: 'image/png', text: 'PNG' },
    { value: 'image/gif', text: 'GIF' },
    { value: 'text/plain', text: 'Texte' },
    { value: 'application/msword', text: 'Word' },
    { value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', text: 'Word (.docx)' },
    { value: 'application/vnd.ms-excel', text: 'Excel' },
    { value: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', text: 'Excel (.xlsx)' }
  ];
  
  // Combiner les types communs avec ceux trouvés dans les documents
  const allTypes = [...new Set([...types, ...commonTypes.map(t => t.value)])];
  
  return [
    { value: null, text: 'Tous les types' },
    ...allTypes.map(t => {
      const commonType = commonTypes.find(ct => ct.value === t);
      return { 
        value: t, 
        text: commonType ? commonType.text : t 
      };
    })
  ];
});

const tagOptions = computed(() => {
  return [
    { value: '', text: 'Tous les tags' },
    ...availableTags.value.map(tag => ({ value: tag, text: tag }))
  ];
});


// Recherche avec debounce
let searchTimeout;
const debouncedSearch = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    searchDocuments();
  }, 500);
};

// Méthodes
async function loadAvailableTags() {
  try {
    const response = await api.get('/document-files/tags');
    availableTags.value = response.data;
  } catch (error) {
    console.error('Erreur lors du chargement des tags:', error);
    availableTags.value = [];
  }
}

function openFileSelector() {
  fileInput.value.click();
}

function handleFileSelection(event) {
  const file = event.target.files[0];
  if (file) {
    // Pré-remplir le formulaire avec les informations du fichier
    uploadForm.value.file = file;
    uploadForm.value.nom = file.name;
    uploadForm.value.type_mime = file.type || 'application/octet-stream';
    
    // Ouvrir la modal
    showUploadModal.value = true;
    
    // Réinitialiser l'input file pour permettre la sélection du même fichier
    event.target.value = '';
  }
}

async function searchDocuments() {
  loading.value = true;
  try {
    const params = {
      page: currentPage.value,
      limit: perPage.value
    };

    // Filtres de base
    if (searchQuery.value.trim()) {
      params.q = searchQuery.value.trim();
    }
    if (selectedMimeType.value) {
      params.type_mime = selectedMimeType.value;
    }

    // Filtre par tag
    if (selectedTag.value) {
      params.tags = selectedTag.value;
    }

    const response = await api.get('/document-files/search', { params });
    documents.value = response.data;
    
    // Mettre à jour la pagination
    if (response.headers['x-total-count']) {
      totalResults.value = parseInt(response.headers['x-total-count']);
      totalPages.value = Math.ceil(totalResults.value / perPage.value);
    }
  } catch (error) {
    console.error('Erreur lors de la recherche:', error);
    alert('Erreur lors de la recherche des documents');
  } finally {
    loading.value = false;
  }
}


function handlePageChange(page) {
  currentPage.value = page;
  searchDocuments();
}

async function uploadDocument() {
  // Validation des champs requis
  if (!uploadForm.value.file) {
    alert('Veuillez sélectionner un fichier');
    return;
  }
  
  if (!uploadForm.value.nom || uploadForm.value.nom.trim() === '') {
    alert('Le nom du document est obligatoire');
    return;
  }
  
  if (!uploadForm.value.type_mime || uploadForm.value.type_mime.trim() === '') {
    alert('Le type MIME est obligatoire');
    return;
  }

  try {
    const formData = new FormData();
    formData.append('file', uploadForm.value.file);
    formData.append('nom', uploadForm.value.nom);
    formData.append('type_mime', uploadForm.value.type_mime);
    formData.append('description', uploadForm.value.description);
    
    // N'envoyer tags que s'il a une valeur
    if (uploadForm.value.tags && uploadForm.value.tags.trim()) {
      formData.append('tags', uploadForm.value.tags);
    }

    await api.post('/document-files', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    showUploadModal.value = false;
    searchDocuments();
    alert('Document uploadé avec succès !');
  } catch (error) {
    console.error('Erreur lors de l\'upload:', error);
    alert('Erreur lors de l\'upload du document');
  }
}

function resetUploadForm() {
  uploadForm.value = {
    file: null,
    nom: '',
    description: '',
    tags: '',
    type_mime: ''
  };
}

function viewDocument(doc) {
  selectedDocument.value = doc;
  showViewModal.value = true;
}

// Nouvelle fonction pour visualiser le contenu du document
async function viewDocumentContent(id) {
  try {
    // D'abord récupérer les métadonnées du fichier
    const fileInfo = await api.get(`/document-files/${id}`);
    const file = fileInfo.data;
    
    // Récupérer le contenu
    const response = await api.get(`/document-files/${id}/content`, {
      responseType: 'blob'
    });
    
    // Créer un blob avec le bon type MIME
    const blob = new Blob([response.data], { 
      type: file.type_mime || 'application/octet-stream' 
    });
    
    // Créer un URL pour le contenu
    const url = window.URL.createObjectURL(blob);
    
    // Gestion spéciale selon le type de fichier
    const fileExtension = file.nom?.split('.').pop()?.toLowerCase();
    const mimeType = file.type_mime || '';
    
    // Pour les PDF, images, et textes, ouvrir directement
    if (mimeType.includes('pdf') || 
        mimeType.includes('image/') || 
        mimeType.includes('text/') ||
        ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'txt', 'html', 'xml'].includes(fileExtension)) {
      window.open(url, '_blank');
    } else {
      // Pour les autres types, proposer le téléchargement
      const link = document.createElement('a');
      link.href = url;
      link.download = file.nom || 'fichier';
      link.click();
    }
    
    // Nettoyer l'URL après un délai
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);
  } catch (error) {
    console.error('Erreur lors de la visualisation:', error);
    
    // Gestion des erreurs spécifiques
    if (error.response?.status === 409) {
      const errorData = error.response.data;
      if (errorData.error === 'Large Object manquant (lo_oid vide)') {
        alert('Erreur: Fichier corrompu - données manquantes');
      } else if (errorData.error === 'Large Object inexistant pour ce fichier') {
        alert('Erreur: Fichier introuvable dans la base de données');
      } else {
        alert('Erreur: ' + errorData.error);
      }
    } else if (error.response?.status === 404) {
      alert('Erreur: Fichier non trouvé');
    } else {
      alert('Erreur lors de la visualisation');
    }
  }
}

async function downloadDocument(id) {
  try {
    const response = await api.get(`/document-files/${id}/download`, {
      responseType: 'blob'
    });
    
    // Créer un lien de téléchargement
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Récupérer le nom du fichier depuis les headers
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'document';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Erreur lors du téléchargement:', error);
    
    // Gestion des erreurs spécifiques
    if (error.response?.status === 409) {
      const errorData = error.response.data;
      if (errorData.error === 'Large Object manquant (lo_oid vide)') {
        alert('Erreur: Fichier corrompu - données manquantes');
      } else if (errorData.error === 'Large Object inexistant pour ce fichier') {
        alert('Erreur: Fichier introuvable dans la base de données');
      } else {
        alert('Erreur: ' + errorData.error);
      }
    } else if (error.response?.status === 404) {
      alert('Erreur: Fichier non trouvé');
    } else {
      alert('Erreur lors du téléchargement');
    }
  }
}

async function deleteDocument(id) {
  if (!confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
    return;
  }
  
  try {
    await api.delete(`/document-files/${id}`);
    searchDocuments();
    alert('Document supprimé avec succès');
  } catch (error) {
    console.error('Erreur lors de la suppression:', error);
    alert('Erreur lors de la suppression');
  }
}

// Utilitaires
function getFileIcon(mimeType) {
  if (mimeType.startsWith('image/')) return 'fas fa-image';
  if (mimeType.startsWith('video/')) return 'fas fa-video';
  if (mimeType.startsWith('audio/')) return 'fas fa-music';
  if (mimeType.includes('pdf')) return 'fas fa-file-pdf';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'fas fa-file-word';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'fas fa-file-excel';
  if (mimeType.includes('text/')) return 'fas fa-file-alt';
  return 'fas fa-file';
}





function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function truncateText(text, maxLength) {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}



// Watchers
watch([selectedMimeType, selectedTag], () => {
  currentPage.value = 1;
  searchDocuments();
});

// Chargement initial
onMounted(() => {
  loadAvailableTags();
  searchDocuments();
});
</script>

<style scoped>
.documentation-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
}

.header-content {
  position: relative;
  text-align: center;
}

.upload-button-container {
  position: absolute;
  top: 0;
  right: 0;
}

.search-filters {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.advanced-search {
  border: 1px solid #dee2e6;
}

.documents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
  padding-bottom: 70px; /* espace pour le footer actions mobile */
}

.document-card {
  transition: transform 0.2s, box-shadow 0.2s;
  border: 1px solid #dee2e6;
}

.document-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
}

.file-icon {
  color: #6c757d;
}

.card-title {
  color: #2768b1;
  margin-bottom: 0.75rem;
  font-weight: 600;
}

.document-meta {
  font-size: 0.9rem;
}

.tags .badge {
  font-size: 0.75rem;
}

.product-info {
  font-size: 0.9rem;
}

.date-info {
  font-size: 0.85rem;
  color: #6c757d;
}

.card-actions {
  margin-top: auto;
}

.btn-group .btn {
  border-radius: 0;
}

.btn-group .btn:first-child {
  border-top-left-radius: 0.375rem;
  border-bottom-left-radius: 0.375rem;
}

.btn-group .btn:last-child {
  border-top-right-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
}

.document-details pre {
  font-size: 0.8rem;
  max-height: 200px;
  overflow-y: auto;
}

.file-info {
  border: 1px solid #dee2e6;
}

.file-info .fa-3x {
  color: #6c757d;
}

.pagination-wrapper {
  display: flex;
  justify-content: center;
}

@media (max-width: 768px) {
  .documentation-container {
    padding: 0 0.5rem;
  }
  
  .header-section .d-flex {
    flex-direction: column;
    gap: 1rem;
  }
  
  .search-filters {
    padding: 0.5rem;
    margin-top: 1rem !important;
  }
  
  .search-filters .form-group {
    margin-bottom: 0.75rem;
  }
  
  .search-filters .form-group label {
    font-size: 0.875rem;
    margin-bottom: 0.25rem;
  }
  
  .search-filters .form-control,
  .search-filters .form-select {
    font-size: 0.875rem;
    padding: 0.375rem 0.5rem;
  }
  
  .documents-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .btn-group {
    display: flex;
    flex-direction: row;
  }
  
  .btn-group .btn {
    border-radius: 0.375rem !important;
    margin-right: 0.25rem;
    flex: 1;
  }
  
  .btn-group .btn:last-child {
    margin-right: 0;
  }
  
  /* Optimiser les boutons d'action */
  .d-flex.justify-content-between {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .d-flex.justify-content-between > div {
    width: 100%;
    text-align: center;
  }
}

@media (max-width: 576px) {
  .search-filters {
    padding: 0.25rem;
  }
  
  .search-filters .form-group label {
    font-size: 0.8rem;
    font-weight: 600;
  }
  
  .search-filters .form-control,
  .search-filters .form-select {
    font-size: 0.8rem;
    padding: 0.25rem 0.375rem;
  }
  
  /* Réduire l'espacement entre les colonnes */
  .search-filters .row {
    margin: 0;
  }
  
  .search-filters .col-6,
  .search-filters .col-12 {
    padding: 0.125rem;
  }
  
  /* Boutons sur très petits écrans */
  .btn-group .btn {
    font-size: 0.75rem;
    padding: 0.25rem 0.375rem;
  }
}
</style> 